<template>
  <form class="view-product-register-module-group" @submit.prevent="submit">
    <bp-input
      v-model="form.name"
      :errors="errors.name"
      id="name"
      placeholder="Ex: Turma A"
      label="Nome da turma"
      description=" "
      required
      :limit="64"
    />

    <bp-input
      v-model="form.description"
      :errors="errors.description"
      id="description"
      textarea
      description=" "
      placeholder="Faça uma breve descrição sobre a turma..."
      label="Descrição"
      :limit="1400"
    />

    <h3>
      Configurações
    </h3>
    <div class="functional-1 text-neutral-400 mb-4">
      Selecione as ofertas que ativarão esta turma
    </div>

    <action-dispatcher action="product/getProduct" :parameters="productId">
      <action-dispatcher
        :action="isSignature ? 'plan/get' : 'offer/get'"
        :parameters="{ product_id: productId, limit: 1000 }"
      >
        <form-group
          v-if="isSignature"
          checkboxes
          v-model="form.plans"
          label="Planos"
          class="fs-12"
          :options="buildPlanOfferOption(plans)"
          :errors="errors.plans"
          id="offer-select"
        />
        <form-group
          v-else
          checkboxes
          v-model="form.offers"
          label="Ofertas"
          class="fs-12"
          :options="buildPlanOfferOption(offers)"
          :errors="errors.offers"
          id="offer-select"
        />
      </action-dispatcher>
    </action-dispatcher>

    <action-dispatcher
      action="membersModules/get"
      :parameters="{
        product_id: $route.params.id,
        limit: 1000
      }"
      @success="successModules"
    >
      <template>
        <h3 class="mt-6">
          Regras de liberação de módulos
        </h3>
        <div class="functional-1 text-neutral-400 mb-7">
          Configure as regras de liberação de módulos para esta turma.
        </div>
        <div v-if="!formModules.length">
          <div class="d-flex">
            <div class="p-3 border rounded border-alert-500 alert-box">
              <div class="d-flex gap-3">
                <bp-icon icon="fa fa-info-circle" class="text-alert-500" />
                <span class="functional-1 text-alert-700">
                  Para configurar regras de liberação de módulos, primeiro
                  cadastre o seu primeiro módulo para este produto.
                </span>
              </div>
            </div>
          </div>
          <bp-button
            variant="outlined"
            label="Cadastrar módulo"
            trailingIcon="fa-light fa-arrow-up-right-from-square"
            route="product-member-area-modules"
            class="my-4"
            size="md"
          />
        </div>
        <div v-else>
          <div class="row mb-4 fs-12 fw-5">
            <div class="col col-md-5 col-lg-6">Nome do módulo</div>
            <div class="col">Regra</div>
          </div>
          <div
            v-for="module in formModules"
            :key="module.id"
            class="row border-bottom border-neutral-100 py-4 align-items-center mx-2 mx-md-0"
          >
            <div class="col-6 col-md-5 col-lg-6 fs-12">{{ module.name }}</div>
            <div class="col-6 col-md-3 col-lg-2">
              <el-select slim v-model="module.option">
                <el-option
                  v-for="item in modulesOptions"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </div>
            <div
              class="col-12 col-md-4 col-lg-4 mt-4 mt-md-0"
              v-if="module.option !== 'release_by_period'"
            >
              <form-group
                v-if="module.option === 'days_to_release'"
                v-model="module.days_to_release"
                id="days_to_release"
                placeholder="0 dias"
                mask="###"
                class="p-0"
              />
              <form-group
                v-if="module.option === 'release_at'"
                v-model="module.release_at"
                is-date
                id="release_at"
                placeholder="00/00/0000"
                mask="##/##/####"
                masked
                class="p-0"
              />
            </div>
            <div
              v-else
              class="col-12 col-md-4 col-lg-4 mt-4 mt-md-0 d-flex align-items-center justify-content-center gap-5"
            >
              <form-group
                v-model="module.release_start_at"
                is-date
                id="release_start_at"
                placeholder="Início"
                mask="##/##/####"
                masked
                :min-date="new Date()"
                class="p-0 w-100"
              />
              <form-group
                v-model="module.release_end_at"
                is-date
                id="release_end_at"
                placeholder="Fim"
                mask="##/##/####"
                masked
                :min-date="transformDate(module.release_start_at)"
                :disabled="!module.release_start_at"
                class="p-0 w-100"
              />
            </div>
          </div>
        </div>
      </template>
    </action-dispatcher>
    <div class="row pt-6">
      <div class="col-lg-2 mb-5">
        <button
          class="btn btn-block btn-outline-secondary"
          type="button"
          @click="$router.go(-1)"
        >
          Cancelar
        </button>
      </div>
      <div class="col-lg-2 offset-lg-8 mb-5">
        <button
          class="btn btn-block btn-new-primary"
          :class="{ 'btn-secondary': loading }"
          :disabled="loading || !isValid"
          type="submit"
        >
          <loading :show="loading">Salvar</loading>
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { formFields, toComputerDate, toHumanDate } from "@/functions/helpers";
import { Option, Select } from "element-ui";
import get from "lodash/get";
import moment from "moment";
import { mapState } from "vuex";

export default {
  components: {
    "el-select": Select,
    "el-option": Option
  },
  props: {
    group: {
      type: Object,
      default: () => ({})
    },
    productId: [Number, String]
  },
  data() {
    return {
      formModules: [],
      ...formFields(["name", "description", "product_id", "offers", "plans"], {
        product_id: parseInt(this.$route.params.id),
        offers: [],
        plans: [],
        ...this.group
      }),
      modulesOptions: [
        { id: "free", name: "Acesso livre" },
        { id: "days_to_release", name: "Liberar após quantidade de dias" },
        { id: "release_at", name: "Liberar no dia" },
        { id: "blocked", name: "Bloqueado" },
        { id: "release_by_period", name: "Liberar por período" }
      ]
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    ...mapState("membersModules", ["modules"]),
    ...mapState("offer", ["offers"]),
    ...mapState("plan", ["plans"]),
    isValid() {
      return (
        this.form?.name?.length &&
        this.form?.name?.length <= 64 &&
        (!this.form.description || this.form?.description?.length < 1401)
      );
    },
    getTitle() {
      return this.group ? "Nova turma" : "Editar turma";
    },
    isSignature() {
      return get(this.product, "type_product") === "signature";
    },
    breadcrumbs() {
      return [
        { link: "product", title: "Meus produtos" },
        {
          link: "product-details",
          title: this.product ? this.product["name"] : "...",
          params: { id: this.$route.params.id }
        },
        {
          link: "product-member-area-groups",
          title: "Turmas",
          params: { id: this.$route.params.id }
        },
        {
          title: this.getTitle
        }
      ];
    }
  },
  methods: {
    transformDate(date) {
      return moment(date, "DD/MM/YYYY hh:mm").toDate();
    },
    buildPlanOfferOption(items) {
      return items.map(item => ({
        id: item.code,
        name: `${item.name} (R$ ${item.price})`
      }));
    },
    successModules() {
      for (let i in this.modules) {
        let module = {
          name: this.modules[i].name,
          module_id: this.modules[i].id,
          option: "free",
          days_to_release: 0,
          release_at: null,
          release_start_at: null,
          release_end_at: null
        };

        // gm = group module
        let gm = get(this.group, "modules", []).find(
          gm => gm.module_id === module.module_id
        );

        if (gm) {
          module.id = gm.id;
          module.option = gm.is_active
            ? gm.release_start_at && gm.release_end_at
              ? "release_by_period"
              : gm.release_at
              ? "release_at"
              : gm.days_to_release
              ? "days_to_release"
              : "free"
            : "blocked";
          module.days_to_release = gm.days_to_release;
          module.release_at = gm.release_at ? toHumanDate(gm.release_at) : null;
          module.release_start_at = gm.release_start_at
            ? toHumanDate(gm.release_start_at)
            : null;
          module.release_end_at = gm.release_end_at
            ? toHumanDate(gm.release_end_at)
            : null;
        }

        this.formModules.push(module);
      }
    },
    submit() {
      this.loading = true;

      const data = { ...this.form };
      let action = "membersGroups/post";

      data.modules = this.formModules.map(m => ({
        ...m,
        is_active: m.option !== "blocked",
        days_to_release: m.option !== "days_to_release" ? 0 : m.days_to_release,
        release_at:
          m.option !== "release_at" ? null : toComputerDate(m.release_at),
        release_start_at:
          m.option !== "release_by_period"
            ? null
            : toComputerDate(m.release_start_at),
        release_end_at:
          m.option !== "release_by_period"
            ? null
            : toComputerDate(m.release_end_at)
      }));

      if (this.group.id) {
        data.id = this.group.id;
        action = "membersGroups/put";
      }

      this.$store
        .dispatch(action, data)
        .then(() => {
          this.$message.success("Salvo com sucesso");
          this.$router.go(-1);
        })
        .catch(error => {
          this.errors = error.response.data;
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_variables.scss";

.view-product-register-module-group {
  h6 {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }

  ::v-deep {
    .form-group {
      input,
      textarea {
        font-size: 14px;
        font-weight: 400;
      }
      &label {
        font-size: 12px;
        color: #5a5466;
        line-height: 24px;
        padding-top: 0;
      }
      .date-picker-wrapper {
        top: 0;
      }
    }
    label {
      margin-bottom: 2px;
    }
    textarea {
      resize: none;
    }
  }
  .alert-box {
    background-color: $alert-024;
  }
}
</style>
